import { FormikContext } from 'formik'
import React, { CSSProperties, useContext, useState } from 'react'
import { useLocalStorage } from 'react-use'

import ActionButton from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface PageAction {
  label: string
  action: () => Promise<unknown> | unknown
  isProcessingText?: string
}

type Props = React.ComponentProps<'div'> & {
  actions: PageAction[]
  showSubmitFormCheckbox?: boolean
}

const PageActions: React.FC<Props> = ({
  actions,
  showSubmitFormCheckbox = true,
  className,
  style,
  ...props
}) => {
  const formikContext = useContext(FormikContext)
  const [isProcessing, setIsProcessing] = useState(false)
  const [shouldSubmitForm, setShouldSubmitForm] = useLocalStorage<boolean>(
    'PageActions.shouldSubmitForm',
    false,
  )
  const [isExpanded, setIsExpanded] = useLocalStorage<boolean>(
    'PageActions.isExpanded',
    false,
  )

  const styles: CSSProperties = {
    ...style,
    position: 'fixed',
    bottom: '0px',
    right: '80px',
    maxWidth: '350px',
  }

  if (!isExpanded) {
    return (
      <Button
        className="z-page-actions"
        variant="destructive"
        size="xs"
        style={styles}
        onClick={() => setIsExpanded(true)}
      >
        Show page actions
      </Button>
    )
  }

  return (
    <div
      className={cn(
        `z-page-actions pointer-events-auto flex flex-col border bg-white px-2
        py-1 text-xs print:hidden`,
        className,
      )}
      style={styles}
      {...props}
    >
      <header className="mb-1 flex flex-col justify-between gap-1 sm:flex-row">
        {formikContext && showSubmitFormCheckbox ? (
          <label htmlFor="submitForm" className="flex items-center py-1">
            <input
              type="checkbox"
              id="submitForm"
              className="mr-2"
              defaultChecked={shouldSubmitForm}
              onChange={(event) => {
                setShouldSubmitForm(event.target.checked)
              }}
            />
            <span>Submit form</span>
          </label>
        ) : (
          <div />
        )}

        <Button
          variant="plain"
          className="text-inherit underline"
          onClick={() => {
            setIsExpanded(false)
          }}
        >
          Hide
        </Button>
      </header>

      {actions.map(
        ({ label, action, isProcessingText = 'Processing' }, index) => {
          return (
            <ActionButton
              key={label}
              onClick={async () => {
                setIsProcessing(true)

                await action()

                if (formikContext && shouldSubmitForm) {
                  await formikContext.submitForm()
                  formikContext.setSubmitting(false)
                }

                setIsProcessing(false)
              }}
              variant="destructive"
              size="xs"
              className={cn({ 'mb-1': index < actions.length - 1 })}
              isProcessing={isProcessing}
              isProcessingText={isProcessingText}
            >
              {label}
            </ActionButton>
          )
        },
      )}
    </div>
  )
}

export default PageActions
